import { ScheduleCheckFileType, ScheduleCheckType } from '@consigli/types';
import clsx from 'clsx';
import { FC, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FaRegCheckCircle, FaRegTimesCircle } from 'react-icons/fa';
import { FiExternalLink } from 'react-icons/fi';
import { HiOutlineTableCells } from 'react-icons/hi2';

export interface ComponentReference {
  type: ScheduleCheckFileType;
  available: boolean;
  page?: number;
  blobId?: string;
  fileName?: string;
  content?: string;
  coordinates?: {
    x0: number;
    y0: number;
    x1: number;
    y1: number;
  };
  cellCoordinates?: {
    sheetName: string;
    row: number[] | null;
    col: number[] | null;
  };
}

export interface ScheduleComponent {
  id: string;
  name: string;
  status: ScheduleCheckType;
  drawings?: ComponentReference;
  schematic?: ComponentReference;
  schedule?: ComponentReference;
}

interface ComponentCardProps {
  component: ScheduleComponent;
  selected?: boolean;
  onPageClick?: (clicked: ComponentReference, schedule?: ComponentReference) => void;
  onScheduleOnlyClick?: (schedule: ComponentReference) => void;
  // Callback-ref factory: registers this card's DOM node by component id so the
  // page can auto-scroll it into view when prev/next navigation makes it active.
  registerCard?: (id: string) => (node: HTMLDivElement | null) => void;
}

const ComponentCardBase: FC<ComponentCardProps> = ({
  component,
  selected = false,
  onPageClick,
  onScheduleOnlyClick,
  registerCard,
}) => {
  const { t } = useTranslation();

  const linkButtonClass = clsx(
    'flex items-center gap-1.5 px-2.5 py-1 text-sm font-medium rounded-md transition-[background-color] duration-150 cursor-pointer',
    selected ? 'text-neutral-on-brand hover:bg-night-light-3' : 'text-gray-900 hover:bg-white',
  );
  const sectionLabelClass = clsx(
    'text-sm font-medium',
    selected ? 'text-day-light-5' : 'text-gray-700',
  );

  const handleReferenceClick = useCallback(
    (clicked?: ComponentReference) => {
      if (clicked?.blobId && clicked?.page) {
        onPageClick?.(clicked, component.schedule);
      }
    },
    [component.schedule, onPageClick],
  );

  const handleSchematicClick = useCallback(() => {
    handleReferenceClick(component.schematic);
  }, [component.schematic, handleReferenceClick]);

  const handleDrawingsClick = useCallback(() => {
    handleReferenceClick(component.drawings);
  }, [component.drawings, handleReferenceClick]);

  const hasClickableDrawings = Boolean(component.drawings?.page && component.drawings?.blobId);
  const hasClickableSchematic = Boolean(component.schematic?.page && component.schematic?.blobId);
  const showScheduleOnlyFallback =
    !hasClickableDrawings && !hasClickableSchematic && Boolean(component.schedule?.blobId);

  const handleScheduleOnlyClick = useCallback(() => {
    if (component.schedule) {
      onScheduleOnlyClick?.(component.schedule);
    }
  }, [component.schedule, onScheduleOnlyClick]);

  return (
    <div
      ref={registerCard?.(component.id)}
      className={clsx(
        'mb-3 p-4 rounded-lg shadow-sm',
        selected ? 'bg-day-dark-1' : 'bg-day-light-3',
      )}
    >
      {/* Header with component name */}
      <div className="mb-3">
        <h3
          className={clsx(
            'text-base font-semibold',
            selected ? 'text-neutral-on-brand' : 'text-gray-900',
          )}
        >
          {component.name}
        </h3>
      </div>

      {/* Schematic and Drawings status */}
      <div className="flex gap-12">
        {/* Schematic - only show if schematic data exists */}
        {component.schematic && (
          <div className="flex flex-col gap-2 min-w-[140px]">
            <div className="flex items-center gap-2">
              {component.schematic.available ? (
                <FaRegCheckCircle className="text-green-600 flex-shrink-0" size={16} />
              ) : (
                <FaRegTimesCircle className="text-red-500 flex-shrink-0" size={16} />
              )}
              <span className={sectionLabelClass}>
                {t('schedule-check-comparison.component-card.schematic')}
              </span>
            </div>
            {component.schematic.page && component.schematic.blobId && (
              <button onClick={handleSchematicClick} className={clsx('ml-0.5', linkButtonClass)}>
                <FiExternalLink size={13} className="flex-shrink-0" />
                <span>
                  {t('schedule-check-comparison.component-card.page')} {component.schematic.page}
                </span>
              </button>
            )}
          </div>
        )}

        {/* Drawings */}
        <div className="flex flex-col gap-2 min-w-[140px]">
          <div className="flex items-center gap-2">
            {component.drawings?.available ? (
              <FaRegCheckCircle className="text-green-600 flex-shrink-0" size={16} />
            ) : (
              <FaRegTimesCircle className="text-red-500 flex-shrink-0" size={16} />
            )}
            <span className={sectionLabelClass}>
              {t('schedule-check-comparison.component-card.drawings')}
            </span>
          </div>
          {component.drawings?.page && component.drawings?.blobId && (
            <button onClick={handleDrawingsClick} className={clsx('ml-0.5', linkButtonClass)}>
              <FiExternalLink size={13} className="flex-shrink-0" />
              <span>
                {t('schedule-check-comparison.component-card.page')} {component.drawings.page}
              </span>
            </button>
          )}
        </div>
      </div>

      {showScheduleOnlyFallback && (
        <button onClick={handleScheduleOnlyClick} className={clsx('mt-3', linkButtonClass)}>
          <HiOutlineTableCells size={14} className="flex-shrink-0" />
          <span>{t('schedule-check-comparison.component-card.view-schedule')}</span>
        </button>
      )}
    </div>
  );
};

// Memoized so that navigating between findings only re-renders the two cards
// whose `selected` flag actually changes, keeping the highlight switch snappy.
export const ComponentCard = memo(ComponentCardBase);
